// An AS number is not an address and has arithmetic of its own: 32 bits, written with or without the `AS` in front,
// and with three stretches the IETF reserved that no registry ever hands out. This is that reading, kept apart from
// parse.ts because nothing an address does applies to it.

export const AS_MAX = 4294967295;

// Blank is a box nobody has finished with rather than a wrong one, which is parse.ts's answer for the same reason.
export type AsResult =
  | { kind: "blank" }
  | { kind: "reading"; number: number }
  | { kind: "error"; message: string };

export const AS_PROBLEM = `An AS number is a whole number from 0 to ${AS_MAX}, written with or without AS in front`;

export function readAsNumber(text: string): AsResult {
  const trimmed = text.trim();
  if (!trimmed) return { kind: "blank" };

  const digits = /^(?:[Aa][Ss])?(\d{1,10})$/.exec(trimmed)?.[1];
  if (digits === undefined) return { kind: "error", message: AS_PROBLEM };

  const number = Number(digits);
  return number <= AS_MAX ? { kind: "reading", number } : { kind: "error", message: AS_PROBLEM };
}

// How an AS number is written wherever one is written down, which is what a reader copies out of the page.
export function writeAsNumber(number: number): string {
  return `AS${number}`;
}

// The two halves of what a 32-bit number used to be, which is worth saying because a 16-bit number is the one every
// older configuration language can hold and the distinction is still how operators talk about them.
export function asWidth(number: number): string {
  return number <= 65535 ? "16-bit" : "32-bit";
}

// The stretches nobody is delegated, which is a fact about the number itself rather than one to be looked up: a
// private AS number appears inside somebody's network and in no registry at all, so it never gets a delegation row.
export function reservedUse(number: number): string {
  for (const [first, last, use] of RESERVED) {
    if (number >= first && number <= last) return use;
  }
  return "";
}

const RESERVED: [number, number, string][] = [
  [64496, 64511, "Documentation and sample code — RFC 5398"],
  [64512, 65534, "Private use — RFC 6996"],
  [65535, 65535, "Reserved — RFC 7300"],
  [4200000000, 4294967294, "Private use — RFC 6996"],
  [4294967295, 4294967295, "Reserved — RFC 7300"],
];
